import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Box, IconButton, Menu, MenuItem, Typography, useTheme, Grid2, Dialog } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { topBarRoutes } from '../../topBarConfig';
import RecaudacionNotification from '../../components/ui/materialui/recaudacionNotification';
import { CheckMutuo } from './CheckMutuo';
import { TipoFuncionalidad } from '../../utils/enums';
import { useUserRolesContext } from '../../context/userRolesContext';

export interface TopBarMenuItem {
    name: string;
    path?: string;
    rol: string[];
    active?: boolean; 
    children?: TopBarMenuItem[]; // opcional
    component?: any
}

export interface TopBarRouteConfig {
    basePath: string;
    origen?: string; // opcional
    match: (path: string) => boolean;
    menus?: TopBarMenuItem[]; // opcional
    subMenus?: TopBarMenuItem[]; // opcional
}

export default function TopBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const [anchorElSubMenu, setAnchorElSubMenu] = useState<null | HTMLElement>(null);
    const [dropdownAnchorEls, setDropdownAnchorEls] = useState<{ [key: number]: HTMLElement | null }>({});

    const [showComponent, setShowComponent] = useState<boolean>(false);
    const [component, setComponent] = useState<any>(<></>);
    const { userPermissions } = useUserRolesContext();
    const userRoles = userPermissions.map(perm => perm.codigoPermiso.toString());
    const origen = location.state?.origen ?? '';

    const currentRouteConfig: TopBarRouteConfig | undefined = topBarRoutes.find(route =>
        route.match(location.pathname) && route.origen === origen);

    if (!currentRouteConfig) return null;

    const hasAccess = (item: any) => {
        if (!item.rol || item.rol.length === 0) return false;
        return item.rol.some((rol: string) => userRoles.includes(rol));
    };

    const resolvePath = (path: string, id: string | undefined) => path.replace(':id', id ?? '');

    const { id } = location.state || { id: undefined };

    const visibleMenus = (currentRouteConfig.menus || []).filter((item: TopBarMenuItem) =>
        item.children?.some(hasAccess) ?? hasAccess(item)
    );

    const visibleSubMenus = currentRouteConfig.subMenus?.filter(hasAccess) || [];

    if (visibleMenus.length === 0 && visibleSubMenus.length === 0) return null;

    const handleDropdownOpen = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
        setDropdownAnchorEls(prev => ({ ...prev, [index]: event.currentTarget }));
    };

    const handleDropdownClose = (index: number) => {
        setDropdownAnchorEls(prev => ({ ...prev, [index]: null }));
    };

    const handleSubMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElSubMenu(event.currentTarget);
    };

    const handleSubMenuClose = () => {
        setAnchorElSubMenu(null);
    };

    const textStyle = {
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: 'pointer',
        padding: '6px 12px',
        borderRadius: '20px',
        transition: 'all 0.3s ease',
        display: 'inline-flex',
        alignItems: 'center',
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
            color: 'white',
        },
        '&:active': {
            backgroundColor: theme.palette.primary.dark,
            color: 'white',
        }
    };

    return (
        <>
            <Dialog
                open={showComponent}
                onClose={() => {setShowComponent(false)}}
                maxWidth="xl"
                fullWidth
            >
                {component}
            </Dialog>
            <Box sx={{ mb: 3 }} gap={2}>
                {/* Notificación de recaudación activa - DISPONIBLE PARA VERSIÓN FUTURA */}
                {/* <RecaudacionNotification /> */}
                
                <Grid2 container justifyContent="center">
                    <Grid2 size={{ xs: 12 }} sx={{ backgroundColor: 'white', p: 1, borderRadius: 2, boxShadow: 1 }}>
                        <Grid2 container alignItems="center">
                            <Grid2 size={{ xs: 11 }} textAlign="center">
                                {visibleMenus.map((menu, index) => (
                                    <React.Fragment key={menu.name}>
                                        {/* {index > 0 && (
                                            <Typography component="span" sx={{ mx: 0 }}>—</Typography>
                                        )} */}
                                        {menu.children ? (
                                            <>
                                                <IconButton onClick={(e) => handleDropdownOpen(e, index)} size="small">
                                                    <Typography component="span" sx={textStyle}>
                                                        {menu.name}
                                                        <ArrowDropDownIcon />
                                                    </Typography>
                                                </IconButton>
                                                <Menu
                                                    anchorEl={dropdownAnchorEls[index]}
                                                    open={Boolean(dropdownAnchorEls[index])}
                                                    onClose={() => handleDropdownClose(index)}
                                                >
                                                    {menu.children
                                                        .filter(hasAccess)
                                                        .map((child: any) => (
                                                            <MenuItem
                                                                key={child.pathName}
                                                                onClick={() => {
                                                                    handleDropdownClose(index);
                                                                    navigate(resolvePath(child.path, id), {
                                                                        state: { ...location.state, id }
                                                                    });
                                                                    if (child.component && !child.path){
                                                                        setShowComponent(true)
                                                                        setComponent(child.component)
                                                                    }
                                                                }}
                                                            >
                                                                {child.name}
                                                            </MenuItem>
                                                        ))}
                                                </Menu>
                                            </>
                                        ) : (
                                            // Menú sin hijos
                                            <Link
                                                to={{
                                                    pathname: resolvePath(menu.path ? menu.path : window.location.pathname, id),
                                                }}
                                                state={{ ...location.state, id }}
                                                style={{ textDecoration: 'none' }}
                                                onClick={() => {
                                                    if (menu.component && !menu.path){
                                                        setShowComponent(true)
                                                        setComponent(menu.component)
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        ...textStyle,
                                                        color: menu.active ? 'black' : textStyle.color,  // Si 'active' es true, el color será negro
                                                    }}>
                                                    {menu.name} {menu.name === "Resumen Mutuo" && menu.component && <CheckMutuo/>}
                                                </Typography>
                                            </Link>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Grid2>

                            <Grid2 size={{ xs: 1 }} textAlign="right">
                                {visibleSubMenus.length > 0 && (
                                    <>
                                        <IconButton onClick={handleSubMenuOpen}>
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorElSubMenu}
                                            open={Boolean(anchorElSubMenu)}
                                            onClose={handleSubMenuClose}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        >
                                            {visibleSubMenus.map((menu, index) => (
                                                <MenuItem
                                                    key={menu.name}
                                                    onClick={() => {
                                                        handleSubMenuClose();
                                                        navigate(resolvePath(menu.path ? menu.path : window.location.pathname, id), {
                                                            state: { ...location.state, id }
                                                        });
                                                    }}
                                                >
                                                    {menu.name}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                )}
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Box>
        </>    
    );
}
